import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebounce } from '../../hooks/useDebounce';
import { useListUrlState } from '../../hooks/useListUrlState';
import axiosInstance from '../../api/axiosInstance';
import { dateFormat } from '../../utils/dateFormat';
import { toast } from 'react-toast';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import {
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  Pagination,
  PriorityBadge,
  SearchInput,
  StatusBadge,
  Tabs,
} from '../ui';
import { parseSourceReference, SourceReferenceBadge } from './SourceReference';

const LIST_SCHEMA = {
  tab: 'epics',
  page: 1,
  page_size: 20,
  search: '',
};

const truncate = (text, max) => {
  const s = String(text || '');
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

/**
 * DEFINE · Epics & Stories — URL-persistent tab/page (refresh keeps stories page 4).
 */
const EpicsStoriesList = ({ projectId: projectIdProp, embedded = false } = {}) => {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();

  const projectId =
    projectIdProp ||
    getProjectIdFromURL() ||
    selectedProject?.id ||
    null;

  const {
    tab: tabParam,
    page,
    page_size: pageSize,
    search,
    setPage,
    setPageSize,
    setSearch,
    setParams,
    pageSizeOptions,
  } = useListUrlState(LIST_SCHEMA, {
    resetPageOn: ['tab', 'search', 'page_size'],
  });

  const activeTab = tabParam === 'stories' ? 'stories' : 'epics';

  const [searchDraft, setSearchDraft] = useState(search);
  const debouncedSearch = useDebounce(searchDraft, 500);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingTestCases, setGeneratingTestCases] = useState({});

  const [epics, setEpics] = useState([]);
  const [epicTotal, setEpicTotal] = useState(0);
  const [stories, setStories] = useState([]);
  const [storyTotal, setStoryTotal] = useState(0);

  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  useEffect(() => {
    if (debouncedSearch !== search) {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEpics = useCallback(
    async (pageNum, pageSz, searchTerm) => {
      if (!projectId) return;
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          project_id: String(projectId),
          page: String(pageNum),
          page_size: String(pageSz),
        });
        if (searchTerm.trim()) params.append('search', searchTerm.trim());

        const response = await axiosInstance.get(`/epics/list?${params.toString()}`);
        if (response && response.status_code === 200) {
          setEpics(response.epics || []);
          setEpicTotal(response.total_count || response.total_epic_count || 0);
        } else {
          throw new Error(response?.message || 'Failed to fetch epics');
        }
      } catch (err) {
        console.error('Error fetching epics:', err);
        setError(err.response?.message || err.message || 'Failed to fetch epics');
        setEpics([]);
        setEpicTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [projectId]
  );

  const fetchStories = useCallback(
    async (pageNum, pageSz, searchTerm) => {
      if (!projectId) return;
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          project_id: String(projectId),
          page: String(pageNum),
          page_size: String(pageSz),
        });
        if (searchTerm.trim()) params.append('search', searchTerm.trim());

        const response = await axiosInstance.get(`/jira_data/stories?${params.toString()}`);
        if (response && response.status_code === 200) {
          setStories(response.stories || []);
          setStoryTotal(response.count || response.total || 0);
        } else {
          throw new Error(response?.message || 'Failed to fetch stories');
        }
      } catch (err) {
        console.error('Error fetching stories:', err);
        setError(err.response?.message || err.message || 'Failed to fetch stories');
        setStories([]);
        setStoryTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [projectId]
  );

  useEffect(() => {
    if (!projectId) return;
    if (search !== '' && search.length < 2) return;
    if (activeTab === 'epics') {
      fetchEpics(page, pageSize, search);
    } else {
      fetchStories(page, pageSize, search);
    }
  }, [projectId, activeTab, page, pageSize, search, fetchEpics, fetchStories]);

  // Prefetch opposite-tab counts for tab badges (page 1, no search)
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        if (activeTab === 'epics' && storyTotal === 0) {
          const res = await axiosInstance.get(
            `/jira_data/stories?project_id=${projectId}&page=1&page_size=1`
          );
          if (!cancelled && res?.status_code === 200) {
            setStoryTotal(res.count || res.total || 0);
          }
        } else if (activeTab === 'stories' && epicTotal === 0) {
          const res = await axiosInstance.get(
            `/epics/list?project_id=${projectId}&page=1&page_size=1`
          );
          if (!cancelled && res?.status_code === 200) {
            setEpicTotal(res.total_count || res.total_epic_count || 0);
          }
        }
      } catch {
        /* badge counts are best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (nextTab) => {
    const tab = nextTab === 'stories' ? 'stories' : 'epics';
    setParams({ tab, page: 1, search: '' });
    setSearchDraft('');
    setError(null);
  };

  const handleGenerateTestCases = async (storyId) => {
    if (!projectId || !storyId) return;
    setGeneratingTestCases((prev) => ({ ...prev, [storyId]: true }));
    try {
      const response = await axiosInstance.post(
        `/test_case/gen_stories_test_cases?project_id=${projectId}&story_id=${storyId}`
      );
      if (response && Number(response.status_code) === 201) {
        const story = stories.find((s) => s.id === storyId);
        const storyKey = story ? story.story_key : storyId;
        toast.success(`Test cases generated for ${storyKey}`);
      } else {
        throw new Error(response?.message || 'Failed to generate test cases');
      }
    } catch (err) {
      console.error('Error generating test cases:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to generate test cases');
    } finally {
      setGeneratingTestCases((prev) => {
        const next = { ...prev };
        delete next[storyId];
        return next;
      });
    }
  };

  const goBackToProjects = () => {
    const workspaceId = getWorkspaceIdFromURL();
    if (workspaceId) {
      navigate(buildWorkspaceDashboardPath(workspaceId, null, 'projects'));
    } else {
      navigate('/dashboard/projects');
    }
  };

  if (!projectId) {
    return (
      <EmptyState
        title="No project selected"
        description="Select a project from the top bar to view epics and stories."
        action={
          !embedded ? (
            <button type="button" className="rt-btn rt-btn--primary" onClick={goBackToProjects}>
              Back to Projects
            </button>
          ) : null
        }
      />
    );
  }

  const epicColumns = [
    {
      key: 'key',
      header: 'Epic',
      width: 120,
      render: (row) => <span className="rt2-link">{row.key}</span>,
    },
    {
      key: 'summary',
      header: 'Summary',
      render: (row) => (
        <div>
          <div title={row.summary}>{truncate(row.summary, 72)}</div>
          {row.description ? (
            <div className="rt2-muted" style={{ marginTop: 2 }} title={row.description}>
              {truncate(row.description, 90)}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status}>{row.status}</StatusBadge>,
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => <PriorityBadge priority={row.priority} />,
    },
    {
      key: 'assignee',
      header: 'Assignee',
      render: (row) => row.assignee || 'Unassigned',
    },
    {
      key: 'points',
      header: 'Points',
      width: 80,
      render: (row) => row.story_point ?? '—',
    },
    {
      key: 'created',
      header: 'Created',
      render: (row) => dateFormat(row.created_at) || '—',
    },
    {
      key: 'updated',
      header: 'Updated',
      render: (row) => dateFormat(row.updated_at) || '—',
    },
  ];

  const storyColumns = [
    {
      key: 'key',
      header: 'Story',
      width: 120,
      render: (row) => <span className="rt2-link">{row.story_key}</span>,
    },
    {
      key: 'summary',
      header: 'Summary',
      render: (row) => {
        const { referenceId, description } = parseSourceReference(row.description);
        return (
          <div>
            <div className="rt-story-summary">
              <span className="rt-story-summary__text" title={row.summary}>
                {truncate(row.summary, 64)}
              </span>
              <SourceReferenceBadge referenceId={referenceId} />
            </div>
            {description ? (
              <div className="rt2-muted" style={{ marginTop: 2 }} title={description}>
                {truncate(description, 80)}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status}>{row.status}</StatusBadge>,
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => <PriorityBadge priority={row.priority} />,
    },
    {
      key: 'assignee',
      header: 'Assignee',
      render: (row) => row.assignee || 'Unassigned',
    },
    {
      key: 'points',
      header: 'Points',
      width: 80,
      render: (row) => row.story_points ?? '—',
    },
    {
      key: 'epic',
      header: 'Epic',
      render: (row) => row.jira_epic_id || '—',
    },
    {
      key: 'updated',
      header: 'Updated',
      render: (row) => dateFormat(row.updated_at) || '—',
    },
    {
      key: 'actions',
      header: 'Actions',
      width: 150,
      render: (row) => (
        <button
          type="button"
          className="rt-btn rt-btn--secondary rt-btn--sm"
          disabled={Boolean(generatingTestCases[row.id])}
          onClick={(e) => {
            e.stopPropagation();
            handleGenerateTestCases(row.id);
          }}
        >
          {generatingTestCases[row.id] ? 'Generating…' : 'Generate tests'}
        </button>
      ),
    },
  ];

  const rows = activeTab === 'epics' ? epics : stories;
  const columns = activeTab === 'epics' ? epicColumns : storyColumns;
  const total = activeTab === 'epics' ? epicTotal : storyTotal;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)) || 1);

  return (
    <div className={`rt-define-panel${embedded ? ' rt-define-panel--embed' : ''}`}>
      {!embedded && (
        <div className="rt-define-panel__intro">
          <button type="button" className="rt-btn rt-btn--tertiary rt-btn--sm" onClick={goBackToProjects}>
            ← Projects
          </button>
          <h1 className="rt-define-panel__title">Epics and Stories</h1>
          <p className="rt2-muted">Browse imported epics and stories for this project.</p>
        </div>
      )}

      <div className="rt-define-toolbar">
        <Tabs
          activeId={activeTab}
          onChange={handleTabChange}
          tabs={[
            { id: 'epics', label: 'Epics', count: epicTotal || undefined },
            { id: 'stories', label: 'Stories', count: storyTotal || undefined },
          ]}
        />
        <FilterBar>
          <SearchInput
            value={searchDraft}
            onChange={setSearchDraft}
            placeholder={`Search ${activeTab} (min 2 chars)…`}
          />
        </FilterBar>
      </div>

      {isLoading && <LoadingState label={`Loading ${activeTab}…`} />}

      {!isLoading && error && !rows.length && (
        <ErrorState
          title={`Unable to load ${activeTab}`}
          description={error}
          onRetry={() =>
            activeTab === 'epics'
              ? fetchEpics(page, pageSize, search)
              : fetchStories(page, pageSize, search)
          }
        />
      )}

      {!isLoading && (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey="id"
            emptyTitle={activeTab === 'epics' ? 'No epics found' : 'No stories found'}
            emptyDescription={
              search
                ? `No ${activeTab} match “${search}”.`
                : `No ${activeTab} available for this project yet.`
            }
          />
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={pageSizeOptions}
            loading={isLoading}
          />
        </>
      )}
    </div>
  );
};

export default EpicsStoriesList;
